import BigNumber from 'bignumber.js';
import { Call } from 'starknet';

import StarknetWallet from '#/features/starknet-wallet/wallet';
import { BridgedToken, type ParaclearContract } from '#/features/paraclear';
import { type StarknetProvider } from '#/features/starknet-wallet/provider';

import { fromQuantums, toQuantums } from '#/utils/quantums';
import { waitForTransaction } from '#/utils/starknet';
import { AsyncResult } from '#/utils/types';

/** 2 ** 128 - 1 */
const INCREASE_ALLOWANCE_MAX_VALUE = BigNumber(2).pow(128).minus(1);

type TxHash = string;

/**
 * Issues a request to deposit funds from L2 account to Paraclear,
 * Takes into account the user's current allowance and the requested
 * deposit amount to also request allowance increase if needed.
 *
 * @param amount Amount to deposit
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 * @param provider Starknet provider
 *
 * @returns Result with transaction hash
 */
export async function requestDepositToParaclear(
  amount: BigNumber,
  token: BridgedToken,
  paraclear: ParaclearContract,
  provider: StarknetProvider,
): AsyncResult<TxHash, TxHash> {
  const allowanceQuantum = await readAllowance(token, paraclear, provider);

  const allowance = fromQuantums(allowanceQuantum, token.decimals);

  const result = await (allowance.isLessThan(amount)
    ? requestDepositWithAllowance(amount, token, paraclear, provider)
    : requestDeposit(amount, token, paraclear, provider));

  return result;
}

/**
 * Queries the current allowance for Paraclear.
 *
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 * @param provider Starknet provider
 *
 * @returns Current allowance for Paraclear
 */
async function readAllowance(
  token: BridgedToken,
  paraclear: ParaclearContract,
  provider: StarknetProvider,
): Promise<BigNumber> {
  return StarknetWallet.getErc20Allowance(
    provider,
    token.l2TokenAddress,
    paraclear.address,
  );
}

/**
 * Requests allowance increase and deposit to
 * Paraclear in a single batch to save time.
 *
 * @param amount Amount to deposit
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 * @param provider Starknet provider
 *
 * @returns Result with transaction hash
 */
async function requestDepositWithAllowance(
  amount: BigNumber,
  token: BridgedToken,
  paraclear: ParaclearContract,
  provider: StarknetProvider,
): AsyncResult<TxHash, TxHash> {
  const allowanceIncreaseCall = buildMaxAllowanceIncreaseCall(token, paraclear);
  const depositToParaclearCall = buildDepositToParaclearCall(
    amount,
    token,
    paraclear,
  );
  const response = await StarknetWallet.executeTransaction(provider, [
    allowanceIncreaseCall,
    depositToParaclearCall,
  ]);
  const result = await waitForTransaction(provider, response.transaction_hash);
  return { ...result, data: response.transaction_hash };
}

/**
 * Requests deposit to Paraclear without
 * requesting allowance increase.
 *
 * @param amount Amount to deposit
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 * @param provider Starknet provider
 *
 * @returns Result with transaction hash
 */
async function requestDeposit(
  amount: BigNumber,
  token: BridgedToken,
  paraclear: ParaclearContract,
  provider: StarknetProvider,
): AsyncResult<TxHash, TxHash> {
  const depositToParaclearCall = buildDepositToParaclearCall(
    amount,
    token,
    paraclear,
  );
  const response = await StarknetWallet.executeTransaction(
    provider,
    depositToParaclearCall,
  );
  const result = await waitForTransaction(provider, response.transaction_hash);
  return { ...result, data: response.transaction_hash };
}

/**
 * Builds a call to increase allowance to the maximum
 * value possible for Paraclear.
 *
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 *
 * @returns Call to increase allowance for Paraclear
 */
function buildMaxAllowanceIncreaseCall(
  token: BridgedToken,
  paraclear: ParaclearContract,
): Call {
  const spender = paraclear.address;

  return {
    contractAddress: token.l2TokenAddress,
    entrypoint: 'increaseAllowance',
    calldata: [spender, INCREASE_ALLOWANCE_MAX_VALUE.toFixed(), '0'],
  };
}

/**
 * Builds a call to deposit funds from L2 account to Paraclear.
 *
 * @param amount Amount to deposit
 * @param token Token to deposit
 * @param paraclear Paraclear contract
 *
 * @returns Call to deposit funds from L2 account to Paraclear
 */
function buildDepositToParaclearCall(
  amount: BigNumber,
  token: BridgedToken,
  paraclear: ParaclearContract,
): Call {
  const tokenAddress = token.l2TokenAddress;
  const amountQuantums = toQuantums(amount, paraclear.decimals);
  return {
    contractAddress: paraclear.address,
    entrypoint: 'deposit',
    calldata: [tokenAddress, amountQuantums],
  };
}
